import { createColumnHelper } from '@tanstack/react-table';
import { OrderListColumns } from '@ldt/features/orders/order_list/shared';
import type { OrderListSearchConfig } from 'src/distribution_platforms/features/order_list';
import { getOrderPatientFullName } from 'src/distribution_platforms/features/utils/patient_utils';
import type { WorkflowDefinition } from 'src/distribution_platforms/features/workflow_definition';
import { toOrderListColumns } from 'src/distribution_platforms/features/workflow_definition';
import {
  LdtOrderListItemFieldsFragment,
  SortBy,
  SpecimenType,
} from 'src/types';
import { SpecimenTypeUtils } from 'src/utils';

export const orderListSearchConfig: OrderListSearchConfig = {
  placeholder: 'Search by Order ID or Patient Name',
  width: '320px',
};

const columnHelper = createColumnHelper<LdtOrderListItemFieldsFragment>();

/**
 * Order list columns for Direct Upload Lab workflow.
 */
export function getOrderListColumns(workflow: WorkflowDefinition) {
  return toOrderListColumns([
    columnHelper.accessor('crossOrgId', {
      id: 'ORDER_ID',
      header: 'ORDER ID#',
      minSize: 180,
      maxSize: 200,
      meta: { isPrimaryColor: true },
      cell: (info) => info.getValue() || '',
    }),
    columnHelper.accessor(
      'specimenType' as keyof LdtOrderListItemFieldsFragment,
      {
        id: OrderListColumns.Type,
        header: 'SPECIMEN TYPE',
        minSize: 220,
        maxSize: 220,
        cell: (info) =>
          info.getValue()
            ? SpecimenTypeUtils.getSpecimenTypeName(
                info.getValue() as SpecimenType,
              )
            : '',
      },
    ),
    columnHelper.accessor(
      (order: LdtOrderListItemFieldsFragment) => getOrderPatientFullName(order),
      {
        id: 'PATIENT',
        header: 'PATIENT',
        minSize: 220,
        maxSize: 260,
        size: 260,
        cell: (info) => info.getValue() || '',
      },
    ),
    columnHelper.accessor(
      (order: LdtOrderListItemFieldsFragment) => order.patient?.dateOfBirthPhi,
      {
        id: 'DATE_OF_BIRTH',
        header: 'DATE OF BIRTH',
        minSize: 158,
        maxSize: 260,
        cell: (info) => info.getValue() ?? '',
      },
    ),
    columnHelper.accessor(
      (order: LdtOrderListItemFieldsFragment) => order.organizationName,
      {
        id: 'CLINIC_NAME',
        header: 'CLINIC NAME',
        minSize: 158,
        maxSize: 260,
        cell: (info) => info.getValue() ?? '',
      },
    ),
    columnHelper.accessor('submittedAt', {
      id: 'SUBMISSION_DATE',
      header: 'SUBMISSION DATE',
      minSize: 200,
      maxSize: 200,
      enableSorting: true,
      sortingFn: 'datetime',
      meta: { sortBy: SortBy.SubmittedAt },
      cell: (info) =>
        info.getValue() ? new Date(info.getValue()!).toLocaleDateString() : '',
    }),
    columnHelper.accessor(
      (order: LdtOrderListItemFieldsFragment) => order.status,
      {
        id: OrderListColumns.Status,
        header: 'STATUS',
        minSize: 180,
        maxSize: 180,
        size: 180,
        enableSorting: false,
        cell: (info) => workflow.getStatusDisplayName(info.getValue()),
      },
    ),
  ]);
}
